import { FaroSwitch } from "@components/common/faro-switch";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { Box, Breakpoint, SvgIcon, Typography } from "@mui/material";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { neutral } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { cardProperties } from "@utils/ui-utils";
import { CSSProperties } from "react";

interface Props {
  /** Integration ID */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /** Callback function triggered when the action button is clicked */
  onActionButtonClick(integrationId: FaroCoreApiTypes.IntegrationId): void;

  /** Whether the integration is enabled in the XG project */
  isEnabled: boolean;

  /** Name of the integration project connected to the XG project */
  projectName?: string;
}

/** Width of the cards depending on the breakpoint */
const integrationCardWidth: {
  [key in Breakpoint]: CSSProperties["width"];
} = {
  xs: "100%",
  sm: "48%",
  md: "48%",
  lg: "32%",
  xl: "32%",
  xxl: "32%",
};

/** Renders a project integration tile */
export function IntegrationTile({
  integrationId,
  isEnabled,
  onActionButtonClick,
  projectName,
}: Props): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        width: integrationCardWidth,
        height: "200px",
        marginTop: cardProperties.marginTop,
        boxShadow: cardProperties.boxShadow,
        borderRadius: cardProperties.borderRadius,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SvgIcon
            inheritViewBox={true}
            component={INTEGRATION_INFO[integrationId].icon}
            sx={{ width: "50px", height: "50px" }}
          />
          <Typography
            sx={{
              color: neutral[800],
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "600",
            }}
          >
            {INTEGRATION_INFO[integrationId].displayName}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: neutral[600],
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "400",
          }}
        >
          {INTEGRATION_INFO[integrationId].description}
        </Typography>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${neutral[200]}`,
          textAlign: "right",
          pt: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: projectName ? "space-between" : "flex-end",
          height: "34px",
        }}
      >
        {projectName && (
          <SphereTooltip
            component="var"
            title={projectName}
            boxProps={{
              sx: {
                ...withEllipsis,
                padding: "8px 0px",
                fontWeight: "400",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: "700",
                ...withEllipsis,
              }}
            >
              Synced with
              <Box
                component="var"
                sx={{
                  ml: "4px",
                }}
              >
                {projectName}
              </Box>
            </Typography>
          </SphereTooltip>
        )}

        <FaroSwitch
          checked={isEnabled}
          onChange={() => onActionButtonClick(integrationId)}
          tooltipTextNotChecked="Enable"
          tooltipTextChecked="Disable"
        />
      </Box>
    </Box>
  );
}
